import React, { useState, useMemo, useEffect } from 'react';
import { Edit2, Trash2, Download, Search, Calendar, Clock, Phone, Mail, MapPin, DollarSign, CheckCircle, AlertCircle, AlertTriangle, ArrowUp, ArrowDown, List, LayoutGrid, ChevronLeft, ChevronRight, Plus, X, RefreshCw } from 'lucide-react';
import AdminNewAppointment from './AdminNewAppointment';

/**
 * BRITE Accounts billing state for one appointment.
 *
 * A booked job with a price gets an estimate in BRITE; marking it completed
 * converts that to an invoice, mints a Stripe pay link and emails the customer.
 * All of that happens automatically — this panel exists to *show* it, and to
 * recover the cases where a step failed quietly.
 *
 * Defined at module scope, not inside AdminAppointments: a component declared
 * inside another component remounts its whole subtree on every parent render
 * (the same trap that made the edit form lose focus on each keystroke).
 *
 * The bulk appointment list deliberately omits pay-link URLs — nothing under
 * /scheduler/api/ checks a session, so it ships `brite_has_payment_link` and
 * this panel fetches the actual URL for one appointment on demand.
 */
const BriteBillingPanel = ({ appointment, onAction }) => {
  const [busy, setBusy] = useState(null);
  const [payLink, setPayLink] = useState(appointment.brite_payment_link || null);
  const [flash, setFlash] = useState(null);

  const estimateNo = appointment.brite_estimate_number;
  const invoiceNo = appointment.brite_invoice_number;
  const hasLink = payLink || appointment.brite_has_payment_link === '1'
    || appointment.brite_has_payment_link === 1 || appointment.brite_has_payment_link === true;
  const error = appointment.brite_error;
  // parseFloat: values from the PHP/MySQL API arrive as strings.
  const price = parseFloat(appointment.final_price || appointment.service_price || 0);

  // Nothing to bill and nothing billed — don't clutter the card.
  if (!estimateNo && !invoiceNo && !error && !(price > 0)) return null;

  const run = async (action, label) => {
    setBusy(action);
    setFlash(null);
    try {
      const result = await onAction(appointment, action);
      if (result?.brite?.brite_payment_link) setPayLink(result.brite.brite_payment_link);
      if (result?.success === false) {
        setFlash({ kind: result.skipped ? 'info' : 'error', text: result.error || 'Failed' });
      } else if (result?.email_error) {
        setFlash({ kind: 'error', text: `Invoice created, but not emailed: ${result.email_error}` });
      } else if (result?.payment_link_error) {
        setFlash({ kind: 'error', text: `Emailed without a pay link: ${result.payment_link_error}` });
      } else if (label) {
        setFlash({ kind: 'ok', text: label });
      }
      return result;
    } catch (e) {
      setFlash({ kind: 'error', text: e.message || 'Request failed' });
      return null;
    } finally {
      setBusy(null);
    }
  };

  const copyPayLink = async () => {
    let url = payLink;
    if (!url) {
      const result = await run('refresh', null);
      url = result?.brite?.brite_payment_link;
      if (!url) {
        setFlash({ kind: 'error', text: 'No pay link on this invoice yet' });
        return;
      }
      setPayLink(url);
    }
    try {
      await navigator.clipboard.writeText(url);
      setFlash({ kind: 'ok', text: 'Pay link copied' });
    } catch {
      // clipboard needs a secure context; fall back so copying still works.
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setFlash({ kind: 'ok', text: 'Pay link copied' });
    }
  };

  const btn = 'px-2 py-1 rounded text-xs disabled:opacity-50 whitespace-nowrap';

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Billing</span>

        {estimateNo && (
          <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 font-mono">{estimateNo}</span>
        )}
        {invoiceNo && (
          <>
            <span className="text-gray-300 text-xs">&rarr;</span>
            <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800 font-mono">{invoiceNo}</span>
          </>
        )}
        {!estimateNo && !invoiceNo && (
          <span className="text-xs text-gray-400 italic">Not in BRITE yet</span>
        )}
        {appointment.brite_invoiced_at && (
          <span className="text-xs text-gray-500">
            invoiced {new Date(appointment.brite_invoiced_at.replace(' ', 'T')).toLocaleDateString()}
          </span>
        )}
        {hasLink && (
          <span className="text-xs text-green-700 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> pay link
          </span>
        )}

        <div className="flex-1" />

        {!estimateNo && !invoiceNo && price > 0 && (
          <button onClick={() => run('create_estimate', 'Estimate created')} disabled={!!busy}
            className={`${btn} bg-amber-600 hover:bg-amber-700 text-white`}
            title="Create the BRITE estimate for this job">
            {busy === 'create_estimate' ? 'Working…' : 'Create Estimate'}
          </button>
        )}

        {!invoiceNo && price > 0 && (
          <button onClick={() => run('create_invoice', 'Invoice sent')} disabled={!!busy}
            className={`${btn} bg-green-600 hover:bg-green-700 text-white`}
            title="Convert to an invoice, add a pay link, and email the customer">
            {busy === 'create_invoice' ? 'Working…' : 'Invoice & Send'}
          </button>
        )}

        {invoiceNo && (
          <>
            <button onClick={copyPayLink} disabled={!!busy}
              className={`${btn} bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300`}
              title="Copy the Stripe pay link to text or email the customer">
              {busy === 'refresh' ? 'Fetching…' : 'Copy Pay Link'}
            </button>
            <button onClick={() => run('resend_invoice', 'Invoice re-sent')} disabled={!!busy}
              className={`${btn} bg-blue-600 hover:bg-blue-700 text-white`}
              title="Email the invoice to the customer again">
              {busy === 'resend_invoice' ? 'Sending…' : 'Resend'}
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="mt-2 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-px flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => run(invoiceNo ? 'resend_invoice' : 'create_invoice', 'Retried')}
            disabled={!!busy} className="underline hover:no-underline flex-shrink-0">
            Retry
          </button>
        </div>
      )}

      {flash && (
        <div className={`mt-2 text-xs rounded px-2 py-1.5 border ${
          flash.kind === 'ok' ? 'text-green-700 bg-green-50 border-green-200'
          : flash.kind === 'info' ? 'text-gray-600 bg-gray-50 border-gray-200'
          : 'text-red-700 bg-red-50 border-red-200'
        }`}>
          {flash.text}
        </div>
      )}
    </div>
  );
};

const AdminAppointments = ({
  appointments,
  customers,
  blockedDates = [],
  setBlockedDates,
  createBlockedDate,
  deleteBlockedDate,
  updateAppointment,
  deleteAppointment,
  services,
  coupons,
  bundles,
  createAppointment,
  adminSettings,
  loadAppointments,
  loadBlockedDates
}) => {
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewMode, setViewMode] = useState('month'); // 'list' | 'day' | 'week' | 'month'
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null); // dateStr of clicked day in calendar
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  const [editCouponError, setEditCouponError] = useState('');
  const [editCouponNote, setEditCouponNote] = useState('');
  // Set by clicking a row in the Scheduled Appointments panel. That panel only
  // renders in the calendar view modes, where no AppointmentDetail is on screen
  // for the clicked row - so the click needs its own surface to open into.
  const [detailAppointment, setDetailAppointment] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // 'list' or 'cards' for the scheduled-appointments panel under the calendar.
  // Remembered so the choice survives navigating away and back.
  const [scheduledView, setScheduledView] = useState(() => {
    try {
      return localStorage.getItem('alltech.scheduledView') || 'list';
    } catch (e) {
      return 'list'; // private browsing / storage disabled
    }
  });
  const [showBlockedDates, setShowBlockedDates] = useState(false);

  const chooseScheduledView = (mode) => {
    setScheduledView(mode);
    try { localStorage.setItem('alltech.scheduledView', mode); } catch (e) { /* non-fatal */ }
  };
  const [syncResult, setSyncResult] = useState(null);

  // Date blocking state
  const [selectedBlockDates, setSelectedBlockDates] = useState([]);
  const [showBlockDateModal, setShowBlockDateModal] = useState(false);
  const [blockDateForm, setBlockDateForm] = useState({
    reason: '',
    blockEntireDate: true,
    selectedTimes: []
  });

  const TIME_SLOTS = [
    '08:00:00', '08:40:00', '09:20:00', '10:00:00', '10:40:00', '11:20:00',
    '12:00:00', '12:40:00', '13:20:00', '14:00:00', '14:40:00', '15:20:00',
    '16:00:00', '16:40:00', '17:20:00', '18:00:00', '18:40:00'
  ];

  // Filter to current year by default
  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState(currentYear);

  // Get available years from appointments
  const availableYears = useMemo(() => {
    const years = new Set(appointments.map(apt => new Date(apt.appointment_date + 'T00:00:00').getFullYear()));
    years.add(currentYear);
    return [...years].sort((a, b) => b - a);
  }, [appointments, currentYear]);

  // Year-filtered appointments (base for everything)
  const yearAppointments = useMemo(() => {
    if (yearFilter === 'all') return appointments;
    return appointments.filter(apt => {
      const aptDate = new Date(apt.appointment_date + 'T00:00:00');
      return aptDate.getFullYear() === yearFilter;
    });
  }, [appointments, yearFilter]);

  // Upcoming work only: still 'scheduled' and not in the past. Deliberately not
  // tied to the search/status filters above - this panel is a standing view of
  // what is coming up, not a second result list for those controls.
  // Always render the modal from the appointment as it currently exists in the
  // list, not the snapshot captured on click - otherwise a save would leave the
  // modal showing the pre-edit values until it was reopened.
  const detailLive = detailAppointment
    ? (appointments || []).find(a => String(a.id) === String(detailAppointment.id))
    : null;

  // Deleting from inside the modal removes the row it was rendering.
  useEffect(() => {
    if (detailAppointment && !detailLive) {
      setDetailAppointment(null);
      setEditingAppointment(null);
    }
  }, [detailAppointment, detailLive]);

  const closeDetailModal = () => {
    setDetailAppointment(null);
    setEditingAppointment(null);
    setEditCouponError('');
    setEditCouponNote('');
  };

  const scheduledUpcoming = useMemo(() => {
    // Local date, not toISOString(): after 5pm Mountain that returns tomorrow's
    // UTC date, which would hide the rest of today's appointments.
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return (appointments || [])
      .filter(apt => apt.status === 'scheduled' && apt.appointment_date >= todayStr)
      .sort((a, b) =>
        (a.appointment_date + a.appointment_time).localeCompare(b.appointment_date + b.appointment_time)
      );
  }, [appointments]);

  const formatDateShort = (dateStr) => {
    // Append a time so the string parses as local; a bare date is treated as
    // UTC and can render as the previous day.
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric'
    });
  };

  const formatTimeDisplay = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Fetch available times when editing starts or date changes
  useEffect(() => {
    if (!editingAppointment || !editingAppointment.appointment_date) {
      setAvailableTimes([]);
      return;
    }
    const apt = editingAppointment;
    let cancelled = false;

    const fetchTimes = async () => {
      setLoadingTimes(true);
      try {
        const serviceIds = apt.service_ids
          ? (typeof apt.service_ids === 'string' ? apt.service_ids : apt.service_ids.join(','))
          : null;
        const serviceParam = serviceIds
          ? `service_ids=${serviceIds}`
          : apt.service_id
            ? `service_id=${apt.service_id}`
            : '';
        const url = `/scheduler/api/availability.php?date=${apt.appointment_date}${serviceParam ? '&' + serviceParam : ''}&exclude_appointment_id=${apt.id}&allow_past=1`;
        const response = await fetch(url);
        const result = await response.json();
        if (cancelled) return;
        if (result.success) {
          const times = (result.data || []).map(timeItem => {
            if (typeof timeItem === 'string') return timeItem;
            if (typeof timeItem === 'object' && timeItem.value) return timeItem.value;
            return null;
          }).filter(t => t !== null);
          // Always include the appointment's current time so it remains selectable
          const currentTime = apt.appointment_time;
          if (currentTime && !times.includes(currentTime)) {
            times.push(currentTime);
            times.sort();
          }
          setAvailableTimes(times);
        } else {
          setAvailableTimes(apt.appointment_time ? [apt.appointment_time] : []);
        }
      } catch (error) {
        console.error('Error loading available times:', error);
        if (!cancelled) {
          setAvailableTimes(apt.appointment_time ? [apt.appointment_time] : []);
        }
      }
      if (!cancelled) setLoadingTimes(false);
    };

    fetchTimes();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingAppointment?.id, editingAppointment?.appointment_date]);

  // The undiscounted total the appointment was booked at. service_price holds
  // the sum of every service on the booking (appointments.php sets it that way
  // for multi-service too), so it is the right basis for a discount.
  const editOriginalPrice = () => parseFloat(editingAppointment?.service_price || 0) || 0;

  // Setting a price also rewrites discount_amount, so the two never disagree.
  const setEditFinalPrice = (value) => {
    setEditingAppointment(prev => {
      const original = parseFloat(prev?.service_price || 0) || 0;
      const final = parseFloat(value);
      return {
        ...prev,
        final_price: value,
        discount_amount: isNaN(final) ? prev.discount_amount : Math.max(0, original - final)
      };
    });
  };

  // Applies a coupon after the fact - the case this exists for is a customer who
  // booked without entering the code they were given.
  //
  // Restrictions here are ADVISORY only. This is the admin screen: the owner is
  // making a deliberate goodwill decision on a job already booked, and a coupon
  // scoped to another service, or one that has since expired, is exactly the kind
  // of thing they need to honour. Anything that would have blocked the customer
  // at booking is surfaced as a note instead. Only a code that does not exist at
  // all is an error, because there is no discount to compute from it.
  const applyEditCoupon = () => {
    const code = (editingAppointment?.coupon_code || '').trim();
    if (!code) {
      setEditCouponError('Enter a coupon code first');
      setEditCouponNote('');
      return;
    }

    const coupon = (coupons || []).find(c => c.code.toLowerCase() === code.toLowerCase());

    if (!coupon) {
      setEditCouponError('No coupon exists with that code');
      setEditCouponNote('');
      return;
    }

    const notes = [];

    if (!coupon.active) {
      notes.push('this coupon is inactive');
    }

    const expiry = coupon.expiry_date || coupon.expiryDate;
    if (expiry && new Date(expiry) <= new Date()) {
      notes.push(`it expired ${new Date(expiry).toLocaleDateString()}`);
    }

    // A multi-service booking carries every service, so check them all before
    // claiming a mismatch - service_id alone is just the primary one.
    if (coupon.service_id) {
      const bookedIds = editingAppointment.service_ids
        ? String(editingAppointment.service_ids).split(',').map(id => id.trim())
        : [String(editingAppointment.service_id)];
      if (!bookedIds.includes(String(coupon.service_id))) {
        const serviceName = (services || []).find(sv => String(sv.id) === String(coupon.service_id))?.name || 'another service';
        notes.push(`it is normally limited to ${serviceName}`);
      }
    }

    const original = editOriginalPrice();
    const type = coupon.discount_type || coupon.type;
    const value = parseFloat(coupon.discount_value ?? coupon.value) || 0;
    const discounted = type === 'percentage' ? original * (1 - value / 100) : original - value;
    const final = Math.max(0, discounted);

    setEditingAppointment(prev => ({
      ...prev,
      coupon_code: coupon.code,
      final_price: final.toFixed(2),
      discount_amount: original - final
    }));
    setEditCouponError('');
    setEditCouponNote(notes.length ? `Applied anyway - ${notes.join(', and ')}.` : '');
  };

  const handleUpdateAppointment = async () => {
    // Email is not checked: a booking taken for a customer with no email address
    // is legitimate, and the phone is what makes them reachable.
    if (!editingAppointment.customer_name || !editingAppointment.customer_phone) {
      return;
    }
    if (!editingAppointment.appointment_date || !editingAppointment.appointment_time) {
      return;
    }
    const finalPrice = parseFloat(editingAppointment.final_price);
    if (isNaN(finalPrice) || finalPrice < 0) {
      alert('Please enter a valid final price.');
      return;
    }
    setIsSubmitting(true);
    const result = await updateAppointment(editingAppointment);
    if (result.success) {
      setEditingAppointment(null);
      setEditCouponError('');
      setEditCouponNote('');
    }
    setIsSubmitting(false);
  };

  const handleDeleteAppointmentConfirm = async (appointmentId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete the appointment for ${customerName}?`)) {
      return;
    }
    setIsSubmitting(true);
    await deleteAppointment(appointmentId);
    setIsSubmitting(false);
  };

  const handleCustomerSelect = (customerId) => {
    if (!customerId || customerId === '') return;
    const selectedCustomer = customers.find(c => c.id === parseInt(customerId));
    if (selectedCustomer) {
      const addressParts = [
        selectedCustomer.address_line1,
        selectedCustomer.address_line2,
        selectedCustomer.city,
        selectedCustomer.state,
        selectedCustomer.zip
      ].filter(part => part && part.trim() !== '');
      const fullAddress = addressParts.join(', ');
      setEditingAppointment(prev => ({
        ...prev,
        customer_name: `${selectedCustomer.first_name} ${selectedCustomer.last_name}`.trim(),
        customer_email: selectedCustomer.email,
        customer_phone: selectedCustomer.phone,
        service_address: fullAddress || prev.service_address
      }));
    }
  };

  const handleMarkCompleted = async (appointment) => {
    if (appointment.status === 'completed') return;
    if (!window.confirm(`Mark appointment for ${appointment.customer_name} as completed?`)) return;
    setIsSubmitting(true);
    await updateAppointment({
      id: appointment.id,
      status: 'completed',
      completion_notes: `Service completed on ${new Date().toLocaleDateString()}`
    });
    setIsSubmitting(false);
  };

  /**
   * Run a BRITE billing action against one appointment.
   *
   * Goes through appointments.php's PUT `brite_action`, which is exactly as
   * privileged as the "Complete" button beside it — that already raises and
   * emails an invoice. This adds no new public surface.
   *
   * `loadAppointments()` afterwards is what refreshes the numbers on the card;
   * the panel keeps the returned pay link locally so Copy works immediately.
   */
  const handleBriteAction = async (appointment, action) => {
    const response = await fetch('/scheduler/api/appointments.php', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: appointment.id, brite_action: action }),
    });
    const result = await response.json().catch(() => ({
      success: false, error: `Server error (${response.status})`,
    }));
    // `refresh` is a read — reloading the whole list for it would close the
    // detail modal the user is standing in.
    if (action !== 'refresh' && loadAppointments) {
      await loadAppointments();
    }
    return result;
  };

  const handleMarkPaid = async (appointment) => {
    if (appointment.payment_status === 'paid') return;
    const dialog = document.createElement('div');
    dialog.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
        <div style="background: white; padding: 24px; border-radius: 12px; max-width: 400px; width: 90%; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
          <h3 style="margin: 0 0 16px 0; font-size: 18px; font-weight: bold; color: #1f2937;">Mark Payment Received</h3>
          <p style="margin: 0 0 16px 0; color: #6b7280;">Customer: <strong>${appointment.customer_name}</strong></p>
          <div style="margin-bottom: 16px;">
            <label style="display: block; margin-bottom: 6px; font-weight: 500; color: #374151; font-size: 14px;">Payment Method</label>
            <select id="paymentMethod" style="width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px;">
              <option value="Venmo">Venmo</option>
              <option value="Cash">Cash</option>
              <option value="Check">Check</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Zelle">Zelle</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div style="margin-bottom: 20px;">
            <label style="display: block; margin-bottom: 6px; font-weight: 500; color: #374151; font-size: 14px;">Payment Amount</label>
            <input type="number" id="paymentAmount" value="${appointment.final_price?.toFixed(2) ?? '0.00'}" step="0.01"
              style="width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px;" />
          </div>
          <div style="display: flex; gap: 8px; justify-content: flex-end;">
            <button id="cancelBtn" style="padding: 8px 16px; background: #e5e7eb; color: #374151; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;">Cancel</button>
            <button id="confirmBtn" style="padding: 8px 16px; background: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;">Mark as Paid</button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(dialog);
    const cleanup = () => document.body.removeChild(dialog);
    dialog.querySelector('#cancelBtn').onclick = cleanup;
    dialog.querySelector('#confirmBtn').onclick = async () => {
      const paymentMethod = dialog.querySelector('#paymentMethod').value;
      const paymentAmount = parseFloat(dialog.querySelector('#paymentAmount').value);
      if (!paymentMethod || !paymentAmount || paymentAmount <= 0) return;
      cleanup();
      setIsSubmitting(true);
      const result = await updateAppointment({
        id: appointment.id,
        payment_status: 'paid',
        payment_method: paymentMethod.trim(),
        payment_amount: paymentAmount
      });
      if (result.success) {
        const updatedAppointment = appointments.find(apt => apt.id === appointment.id);
        if (updatedAppointment) {
          updatedAppointment.payment_status = 'paid';
          updatedAppointment.payment_method = paymentMethod.trim();
          updatedAppointment.payment_amount = paymentAmount;
        }
        setRefreshKey(prev => prev + 1);
      }
      setIsSubmitting(false);
    };
  };

  const handleUnmarkPaid = async (appointment) => {
    if (appointment.payment_status !== 'paid') return;
    if (!window.confirm(
      `Remove payment record for ${appointment.customer_name}?\n\n` +
      `This will unmark the $${appointment.payment_amount?.toFixed(2) ?? appointment.final_price} payment via ${appointment.payment_method || 'Unknown method'}.`
    )) return;
    setIsSubmitting(true);
    const result = await updateAppointment({
      id: appointment.id,
      payment_status: 'pending',
      payment_method: null,
      payment_amount: null
    });
    if (result.success) {
      const updatedAppointment = appointments.find(apt => apt.id === appointment.id);
      if (updatedAppointment) {
        updatedAppointment.payment_status = 'pending';
        updatedAppointment.payment_method = null;
        updatedAppointment.payment_amount = null;
      }
      setRefreshKey(prev => prev + 1);
    }
    setIsSubmitting(false);
  };

  // ========== Date Blocking Functions ==========

  const handleTimeSelection = (timeStr) => {
    setBlockDateForm(prev => ({
      ...prev,
      selectedTimes: prev.selectedTimes.includes(timeStr)
        ? prev.selectedTimes.filter(t => t !== timeStr)
        : [...prev.selectedTimes, timeStr].sort()
    }));
  };

  const blockSelectedDates = async () => {
    if (selectedBlockDates.length === 0 || !blockDateForm.reason.trim()) return;
    if (!blockDateForm.blockEntireDate && blockDateForm.selectedTimes.length === 0) return;
    if (!createBlockedDate) return;

    setIsSubmitting(true);
    const datesToBlock = selectedBlockDates.filter(dateStr =>
      !blockedDates.some(blocked => blocked.date === dateStr)
    );

    for (const dateStr of datesToBlock) {
      try {
        const blockData = { date: dateStr, reason: blockDateForm.reason.trim() };
        if (!blockDateForm.blockEntireDate) {
          blockData.blocked_times = blockDateForm.selectedTimes;
        }
        await createBlockedDate(blockData);
      } catch (error) {
        console.error(`Error blocking ${dateStr}:`, error);
      }
    }

    setSelectedBlockDates([]);
    setBlockDateForm({ reason: '', blockEntireDate: true, selectedTimes: [] });
    setShowBlockDateModal(false);
    setIsSubmitting(false);
  };

  const unblockDate = async (blockedDateId) => {
    if (!window.confirm('Are you sure you want to unblock this date?')) return;
    if (!deleteBlockedDate) return;
    try {
      await deleteBlockedDate(blockedDateId);
    } catch (error) {
      console.error('Error unblocking date:', error);
    }
  };

  const addCommonHolidays = async () => {
    if (!createBlockedDate) return;
    const year = calendarDate.getFullYear();
    const holidays = [
      { date: `${year}-01-01`, reason: "New Year's Day" },
      { date: `${year}-07-04`, reason: "Independence Day" },
      { date: `${year}-12-25`, reason: "Christmas Day" },
      { date: `${year}-11-28`, reason: "Thanksgiving" },
      { date: `${year}-12-24`, reason: "Christmas Eve" },
      { date: `${year}-12-31`, reason: "New Year's Eve" }
    ];
    const newHolidays = holidays.filter(h => !blockedDates.some(b => b.date === h.date));
    if (newHolidays.length === 0) return;

    setIsSubmitting(true);
    for (const holiday of newHolidays) {
      try { await createBlockedDate(holiday); } catch (e) { /* silent */ }
    }
    setIsSubmitting(false);
  };

  const clearBlockSelection = () => {
    setSelectedBlockDates([]);
    setBlockDateForm({ reason: '', blockEntireDate: true, selectedTimes: [] });
  };

  // Pull fresh data from the database. Distinct from "Sync Calendar", which
  // pushes appointments to Google - this just re-reads what is already stored,
  // for when a booking came in while the page was open.
  const refreshData = async () => {
    setRefreshing(true);
    try {
      const jobs = [];
      if (typeof loadAppointments === 'function') jobs.push(loadAppointments());
      if (typeof loadBlockedDates === 'function') jobs.push(loadBlockedDates());
      await Promise.all(jobs);
      setRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const syncGoogleCalendar = async () => {
    if (syncingCalendar) return;
    setSyncingCalendar(true);
    setSyncResult(null);
    try {
      const response = await fetch('/scheduler/api/sync-google-calendar.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      setSyncResult(result);
      // Auto-dismiss success message after 5 seconds
      if (result.success) {
        setTimeout(() => setSyncResult(null), 5000);
      }
    } catch (error) {
      setSyncResult({ success: false, error: 'Network error: ' + error.message });
    } finally {
      setSyncingCalendar(false);
    }
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatShortDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'no_show': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRevenue = () => {
    return yearAppointments
      .filter(apt => apt.payment_status === 'paid')
      .reduce((total, apt) => total + (apt.payment_amount || apt.final_price || apt.service_price), 0);
  };

  const getUpcomingCount = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return yearAppointments.filter(apt =>
      apt.status === 'scheduled' &&
      new Date(apt.appointment_date + 'T00:00:00') >= today
    ).length;
  };

  const getCompletedCount = () => {
    return yearAppointments.filter(apt => apt.status === 'completed').length;
  };

  const getPendingPaymentCount = () => {
    return yearAppointments.filter(apt =>
      apt.status === 'completed' && apt.payment_status === 'pending'
    ).length;
  };

  const uniqueServices = [...new Set(yearAppointments.map(apt => apt.service_name))].sort();

  const filteredAppointments = yearAppointments
    .filter(appointment => {
      const matchesSearch = searchTerm === '' ||
        appointment.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (appointment.customer_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        appointment.service_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || appointment.status === statusFilter;
      const matchesPayment = paymentFilter === 'all' || appointment.payment_status === paymentFilter;
      const matchesService = serviceFilter === 'all' || appointment.service_name === serviceFilter;
      return matchesSearch && matchesStatus && matchesPayment && matchesService;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.appointment_date + ' ' + a.appointment_time) - new Date(b.appointment_date + ' ' + b.appointment_time);
          break;
        case 'name':
          comparison = a.customer_name.localeCompare(b.customer_name);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'service':
          comparison = a.service_name.localeCompare(b.service_name);
          break;
        case 'payment':
          comparison = a.payment_status.localeCompare(b.payment_status);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // ========== Calendar Helpers ==========

  const getDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getAppointmentsForDate = (dateStr) => {
    return filteredAppointments.filter(apt => apt.appointment_date === dateStr);
  };

  const navigateCalendar = (direction) => {
    const d = new Date(calendarDate);
    if (viewMode === 'day') {
      d.setDate(d.getDate() + direction);
    } else if (viewMode === 'week') {
      d.setDate(d.getDate() + (7 * direction));
    } else if (viewMode === 'month') {
      d.setMonth(d.getMonth() + direction);
    }
    setCalendarDate(d);
  };

  const goToToday = () => {
    setCalendarDate(new Date());
  };

  const getCalendarTitle = () => {
    if (viewMode === 'day') {
      return calendarDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    } else if (viewMode === 'week') {
      const start = new Date(calendarDate);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return calendarDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  const getWeekDays = () => {
    const start = new Date(calendarDate);
    start.setDate(start.getDate() - start.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const getMonthDays = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    const days = [];
    const current = new Date(startDate);
    while (current <= lastDay || current.getDay() !== 0) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
      if (days.length >= 42) break; // max 6 weeks
    }
    return days;
  };

  const todayStr = getDateStr(new Date());

  // ========== Appointment Card (compact, for calendar cells) ==========
  const renderAppointmentMini = (apt) => (
    <div
      key={apt.id}
      className={`text-xs p-1.5 rounded mb-1 cursor-pointer truncate ${
        apt.status === 'completed' ? 'bg-green-50 border-l-2 border-green-500' :
        apt.status === 'cancelled' ? 'bg-red-50 border-l-2 border-red-400 line-through' :
        apt.status === 'no_show' ? 'bg-yellow-50 border-l-2 border-yellow-400' :
        'bg-blue-50 border-l-2 border-blue-500'
      }`}
      title={`${apt.customer_name} - ${apt.service_name} at ${formatTime(apt.appointment_time)}`}
    >
      <span className="font-medium">{formatTime(apt.appointment_time)}</span>{' '}
      <span className="text-gray-600">{apt.customer_name.split(' ')[0]}</span>
    </div>
  );

  // ========== Appointment Detail Card (for day view / expanded) ==========
  // Called as a plain function, NOT rendered as <AppointmentDetail />. Defining a
  // component inside another component gives it a new function identity on every
  // parent render, so React sees a different element type, unmounts the whole
  // subtree and mounts a fresh one. That destroyed the focused <input> after every
  // keystroke - you could type one character, then had to click back into the box.
  // Calling it inlines the JSX into the parent's tree, so the DOM nodes reconcile
  // normally and focus survives. Do not convert this back to JSX.
  // (It must stay hook-free for the same reason - there are none today.)
  const renderAppointmentDetail = (appointment) => (
    <div key={`${appointment.id}-${refreshKey}-${appointment.payment_status}`} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {editingAppointment && editingAppointment.id === appointment.id ? (
        <div className="space-y-3">
          {customers && customers.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Select Existing Customer (optional)</label>
              <select
                onChange={(e) => handleCustomerSelect(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-blue-50"
                disabled={isSubmitting}
              >
                <option value="">-- Select a customer to auto-fill --</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.first_name} {customer.last_name} - {customer.email}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Customer Name</label>
              <input type="text" value={editingAppointment.customer_name}
                onChange={(e) => setEditingAppointment(prev => ({...prev, customer_name: e.target.value}))}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
              <input type="tel" value={editingAppointment.customer_phone}
                onChange={(e) => setEditingAppointment(prev => ({...prev, customer_phone: e.target.value}))}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={editingAppointment.customer_email || ''} placeholder="No email on file"
              onChange={(e) => setEditingAppointment(prev => ({...prev, customer_email: e.target.value}))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Service Address</label>
            <textarea value={editingAppointment.service_address}
              onChange={(e) => setEditingAppointment(prev => ({...prev, service_address: e.target.value}))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm" rows="2" disabled={isSubmitting} />
          </div>
          <div className="bg-blue-50 p-3 rounded mb-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Scheduled Service</label>
            <p className="text-sm font-semibold text-blue-800">{editingAppointment.service_name || 'Unknown Service'}</p>
            {editingAppointment.total_duration && (
              <p className="text-xs text-gray-500">Duration: {editingAppointment.total_duration} min</p>
            )}
          </div>
          <div className="bg-amber-50 border border-amber-200 p-3 rounded space-y-2">
            <label className="block text-xs font-semibold text-gray-700">Pricing</label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Original Price</label>
                <input type="text" readOnly value={`$${editOriginalPrice().toFixed(2)}`}
                  className="w-full border border-gray-200 bg-gray-100 text-gray-600 rounded px-2 py-1 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Coupon Code</label>
                <div className="flex gap-1">
                  <input type="text" value={editingAppointment.coupon_code || ''}
                    onChange={(e) => { setEditingAppointment(prev => ({...prev, coupon_code: e.target.value})); setEditCouponError(''); setEditCouponNote(''); }}
                    placeholder="Code"
                    className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting} />
                  <button type="button" onClick={applyEditCoupon}
                    className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs whitespace-nowrap disabled:opacity-50"
                    disabled={isSubmitting}>Apply</button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Final Price</label>
                <input type="number" step="0.01" min="0"
                  value={editingAppointment.final_price ?? ''}
                  onChange={(e) => setEditFinalPrice(e.target.value)}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting} />
              </div>
            </div>
            {editCouponError && <p className="text-red-600 text-xs">{editCouponError}</p>}
            {editCouponNote && <p className="text-amber-700 text-xs">{editCouponNote}</p>}
            {(parseFloat(editingAppointment.discount_amount) || 0) > 0 && (
              <p className="text-xs text-green-700">
                Discount applied: -${(parseFloat(editingAppointment.discount_amount) || 0).toFixed(2)}
                {editingAppointment.coupon_code ? ` (${editingAppointment.coupon_code})` : ''}
              </p>
            )}
            {editingAppointment.payment_status === 'paid' && (
              <p className="text-xs text-gray-500">
                This appointment is marked paid - update Payment Amount below too if the amount collected changed.
              </p>
            )}
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
              <input type="date" value={editingAppointment.appointment_date}
                onChange={(e) => setEditingAppointment(prev => ({...prev, appointment_date: e.target.value}))}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Time</label>
              {loadingTimes ? (
                <div className="w-full border border-gray-300 rounded px-2 py-1 text-sm text-gray-400 bg-gray-50">Loading...</div>
              ) : (
                <select value={editingAppointment.appointment_time}
                  onChange={(e) => setEditingAppointment(prev => ({...prev, appointment_time: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting}>
                  {availableTimes.length > 0 ? (
                    availableTimes.map(time => (
                      <option key={time} value={time}>
                        {formatTimeDisplay(time)}
                        {time === appointment.appointment_time ? ' (current)' : ''}
                      </option>
                    ))
                  ) : (
                    <option value={editingAppointment.appointment_time}>
                      {formatTimeDisplay(editingAppointment.appointment_time)} (current)
                    </option>
                  )}
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select value={editingAppointment.status}
                onChange={(e) => setEditingAppointment(prev => ({...prev, status: e.target.value}))}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting}>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="no_show">No Show</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Payment</label>
              <select value={editingAppointment.payment_status || 'pending'}
                onChange={(e) => setEditingAppointment(prev => ({...prev, payment_status: e.target.value}))}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting}>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>
          {editingAppointment.payment_status === 'paid' && (
            <div className="grid grid-cols-2 gap-3 bg-green-50 p-3 rounded">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={editingAppointment.payment_method || ''}
                  onChange={(e) => setEditingAppointment(prev => ({...prev, payment_method: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting}>
                  <option value="">Select method</option>
                  <option value="Venmo">Venmo</option>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="Credit Card">Credit Card</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount</label>
                <input type="number" step="0.01"
                  value={editingAppointment.payment_amount || editingAppointment.final_price || ''}
                  onChange={(e) => setEditingAppointment(prev => ({...prev, payment_amount: e.target.value}))}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm" disabled={isSubmitting} />
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={editingAppointment.notes || ''}
              onChange={(e) => setEditingAppointment(prev => ({...prev, notes: e.target.value}))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm" rows="2" disabled={isSubmitting} />
          </div>
          {editingAppointment.status === 'completed' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Completion Notes</label>
              <textarea value={editingAppointment.completion_notes || ''}
                onChange={(e) => setEditingAppointment(prev => ({...prev, completion_notes: e.target.value}))}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" rows="2"
                placeholder="Service completion details..." disabled={isSubmitting} />
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button onClick={handleUpdateAppointment}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
              disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
            <button onClick={() => { setEditingAppointment(null); setEditCouponError(''); setEditCouponNote(''); }}
              className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-sm"
              disabled={isSubmitting}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-start">
          <div className="flex-1 grid md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-semibold text-gray-800">{appointment.customer_name}</h4>
                <span className={`text-xs px-2 py-1 rounded ${getStatusColor(appointment.status)}`}>{appointment.status}</span>
                <span className={`text-xs px-2 py-1 rounded ${getPaymentStatusColor(appointment.payment_status)}`}>
                  {appointment.payment_status === 'paid' ? 'Paid' : 'Pending'}
                </span>
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center gap-2"><Phone className="w-3 h-3" /><span>{appointment.customer_phone}</span></div>
                <div className="flex items-center gap-2"><Mail className="w-3 h-3" /><span className={appointment.customer_email ? '' : 'italic text-gray-400'}>{appointment.customer_email || 'No email'}</span></div>
                <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /><span className="truncate">{appointment.service_address}</span></div>
              </div>
            </div>
            <div>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-3 h-3 text-gray-400" />
                  <span className="font-medium">{formatShortDate(appointment.appointment_date)}</span>
                  <Clock className="w-3 h-3 text-gray-400 ml-2" />
                  <span>{formatTime(appointment.appointment_time)}</span>
                </div>
                <p className="text-gray-600"><strong>Service:</strong> {appointment.service_name}</p>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-3 h-3 text-green-600" />
                  <span className="font-semibold text-green-600">
                    ${(appointment.payment_amount || appointment.final_price || appointment.service_price).toFixed(2)}
                  </span>
                  {appointment.payment_status === 'paid' && appointment.payment_method && (
                    <span className="text-xs text-gray-500">via {appointment.payment_method}</span>
                  )}
                </div>
                {appointment.coupon_code && <p className="text-xs text-gray-500">Coupon: {appointment.coupon_code}</p>}
                {appointment.notes && <p className="text-xs text-gray-500 italic">"{appointment.notes}"</p>}
                {appointment.completion_notes && <p className="text-xs text-green-600 italic">Completed: {appointment.completion_notes}</p>}
                {appointment.payment_date && <p className="text-xs text-gray-500">Paid: {new Date(appointment.payment_date).toLocaleDateString()}</p>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 ml-4">
            {appointment.status === 'scheduled' && (
              <button onClick={() => handleMarkCompleted(appointment)}
                className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                disabled={isSubmitting} title="Mark as completed">
                ✓ Complete
              </button>
            )}
            {appointment.payment_status !== 'paid' ? (
              <button onClick={() => handleMarkPaid(appointment)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                disabled={isSubmitting} title="Mark as paid">
                Mark Paid
              </button>
            ) : (
              <button onClick={() => handleUnmarkPaid(appointment)}
                className="bg-orange-600 hover:bg-orange-700 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                disabled={isSubmitting} title="Remove payment record">
                Unmark Paid
              </button>
            )}
            <button onClick={() => setEditingAppointment(appointment)}
              className="text-blue-600 hover:text-blue-800 p-1" disabled={isSubmitting} title="Edit appointment">
              <Edit2 className="w-4 h-4" />
            </button>
            <button onClick={() => handleDeleteAppointmentConfirm(appointment.id, appointment.customer_name)}
              className="text-red-600 hover:text-red-800 p-1" disabled={isSubmitting} title="Delete appointment">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {!(editingAppointment && editingAppointment.id === appointment.id) && (
        <BriteBillingPanel
          key={`brite-${appointment.id}-${appointment.brite_invoice_number || ''}-${appointment.brite_estimate_number || ''}`}
          appointment={appointment}
          onAction={handleBriteAction}
        />
      )}
    </div>
  );

  // ========== Calendar Views ==========
  // These are render functions, not components, for the same reason as
  // renderAppointmentDetail above: a component defined inside another component
  // remounts its entire subtree on every parent render, which wiped out the
  // focused input in the edit form on each keystroke. Keep them as calls.

  const renderDayView = () => {
    const dateStr = getDateStr(calendarDate);
    const dayAppointments = getAppointmentsForDate(dateStr)
      .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time));

    return (
      <div className="space-y-3">
        {dayAppointments.length === 0 ? (
          <p className="text-gray-500 text-center py-12">No appointments on this day</p>
        ) : (
          dayAppointments.map(apt => renderAppointmentDetail(apt))
        )}
      </div>
    );
  };

  const renderWeekView = () => {
    const days = getWeekDays();
    return (
      <div>
        <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="bg-gray-100 text-center text-sm font-semibold text-gray-700 py-3">{day}</div>
          ))}
          {days.map(day => {
            const dateStr = getDateStr(day);
            const dayApts = getAppointmentsForDate(dateStr)
              .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time));
            const isToday = dateStr === todayStr;
            const isSelected = selectedDay === dateStr;

            return (
              <div
                key={dateStr}
                className={`bg-white p-2 cursor-pointer hover:bg-blue-50 transition-colors ${
                  isToday ? 'ring-2 ring-inset ring-blue-400' : ''
                } ${isSelected ? 'bg-blue-50 ring-2 ring-inset ring-green-500' : ''
                } ${dayApts.length > 0 ? 'bg-green-50' : ''}`}
                style={{ minHeight: '140px' }}
                onClick={() => setSelectedDay(dateStr === selectedDay ? null : dateStr)}
              >
                <div className={`text-lg font-semibold mb-1 ${isToday ? 'text-blue-600' : 'text-gray-800'}`}>
                  {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
                <div className="space-y-0.5">
                  {dayApts.slice(0, 5).map(apt => renderAppointmentMini(apt))}
                  {dayApts.length > 5 && (
                    <div className="text-xs text-gray-500 text-center">+{dayApts.length - 5} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected Day Appointments */}
        {selectedDay && (
          <div className="mt-6">
            <h4 className="text-lg font-bold text-gray-800 mb-3">
              {new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({getAppointmentsForDate(selectedDay).length} appointment{getAppointmentsForDate(selectedDay).length !== 1 ? 's' : ''})
              </span>
            </h4>
            <div className="space-y-3">
              {getAppointmentsForDate(selectedDay)
                .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time))
                .map(apt => renderAppointmentDetail(apt))}
              {getAppointmentsForDate(selectedDay).length === 0 && (
                <p className="text-gray-500 text-center py-6">No appointments on this day</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMonthView = () => {
    const days = getMonthDays();
    const currentMonth = calendarDate.getMonth();

    const handleMonthDayClick = (dateStr, event) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const clickedDate = new Date(dateStr + 'T00:00:00');
      const isFuture = clickedDate >= today;

      // Cmd/Ctrl+click for multi-select blocking (future dates only)
      if ((event.metaKey || event.ctrlKey) && isFuture) {
        setSelectedBlockDates(prev =>
          prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
        );
        return;
      }

      // Normal click: view day details
      setSelectedDay(dateStr === selectedDay ? null : dateStr);
    };

    return (
      <div>
        {/* Date Blocking Controls */}
        <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
          <span className="text-sm text-gray-600">
            Hold <kbd className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">Cmd</kbd>/<kbd className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">Ctrl</kbd>+click to select dates for blocking
          </span>
          <div className="flex-1"></div>
          {selectedBlockDates.length > 0 && (
            <>
              <span className="text-sm font-medium text-green-700">{selectedBlockDates.length} selected</span>
              <button onClick={clearBlockSelection}
                className="bg-gray-400 hover:bg-gray-500 text-white px-3 py-1.5 rounded-lg text-sm">
                Clear
              </button>
              <button onClick={() => setShowBlockDateModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1">
                Block {selectedBlockDates.length} Date{selectedBlockDates.length !== 1 ? 's' : ''}
              </button>
            </>
          )}
          <button onClick={addCommonHolidays} disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm">
            {isSubmitting ? 'Adding...' : 'Add Holidays'}
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="bg-gray-100 text-center text-sm font-semibold text-gray-700 py-3">{day}</div>
          ))}
          {days.map(day => {
            const dateStr = getDateStr(day);
            const dayApts = getAppointmentsForDate(dateStr)
              .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time));
            const isToday = dateStr === todayStr;
            const isCurrentMonth = day.getMonth() === currentMonth;
            const isSelected = selectedDay === dateStr;
            const isBlockSelected = selectedBlockDates.includes(dateStr);
            const blockedInfo = blockedDates.find(bd => bd.date === dateStr);
            const isBlocked = blockedInfo && blockedInfo.is_entire_date_blocked;
            const isPartiallyBlocked = blockedInfo && !blockedInfo.is_entire_date_blocked;

            return (
              <div
                key={dateStr}
                className={`p-2 cursor-pointer transition-colors border ${
                  isBlockSelected ? 'bg-green-200 hover:bg-green-300 border-green-400' :
                  isBlocked ? 'bg-red-50 hover:bg-red-100' :
                  isPartiallyBlocked ? 'bg-yellow-50 hover:bg-yellow-100' :
                  isSelected ? 'bg-blue-50' :
                  dayApts.length > 0 && isCurrentMonth ? 'bg-green-50 hover:bg-blue-50' :
                  'bg-white hover:bg-blue-50'
                } ${isToday ? 'ring-2 ring-inset ring-blue-400' : ''
                } ${isSelected && !isBlockSelected ? 'ring-2 ring-inset ring-green-500' : ''
                } ${!isCurrentMonth ? 'opacity-30' : ''}`}
                style={{ minHeight: '100px' }}
                onClick={(e) => handleMonthDayClick(dateStr, e)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-lg font-semibold ${
                    isToday ? 'text-blue-600' : isSelected ? 'text-green-700' : 'text-gray-800'
                  }`}>
                    {day.getDate()}
                  </span>
                  {isBlockSelected && <span className="text-green-700 text-xs font-bold">&#10003;</span>}
                  {isBlocked && <AlertTriangle className="w-4 h-4 text-red-500" />}
                  {isPartiallyBlocked && <Clock className="w-4 h-4 text-yellow-600" />}
                </div>
                {isBlocked && blockedInfo.reason && (
                  <div className="text-xs text-red-600 font-medium truncate mb-0.5">{blockedInfo.reason}</div>
                )}
                {isPartiallyBlocked && (
                  <div className="text-xs text-yellow-700 font-medium mb-0.5">{blockedInfo.blocked_time_count} time{blockedInfo.blocked_time_count !== 1 ? 's' : ''} blocked</div>
                )}
                {dayApts.length > 0 && (
                  <div className="space-y-0.5">
                    {dayApts.slice(0, 3).map(apt => (
                      <div key={apt.id} className={`text-xs px-1.5 py-0.5 rounded truncate ${
                        apt.status === 'completed' ? 'bg-green-100 text-green-800' :
                        apt.status === 'cancelled' ? 'bg-red-100 text-red-700 line-through' :
                        apt.status === 'no_show' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {formatTime(apt.appointment_time)} {apt.customer_name.split(' ')[0]}
                      </div>
                    ))}
                    {dayApts.length > 3 && (
                      <div className="text-xs text-gray-500 font-medium text-center">+{dayApts.length - 3} more</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-600">
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span> Scheduled</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span> Completed</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-300"></span> Cancelled</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300"></span> No Show</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-300"></span> Blocked</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-50 border border-yellow-300"></span> Partially Blocked</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 border border-green-400"></span> Selected for Blocking</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 rounded ring-2 ring-blue-400"></span> Today</div>
        </div>

        {/* Selected Day Details */}
        {selectedDay && (() => {
          const dayBlockedInfo = blockedDates.find(bd => bd.date === selectedDay);
          const dayApts = getAppointmentsForDate(selectedDay)
            .sort((a, b) => a.appointment_time.localeCompare(b.appointment_time));
          return (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-bold text-gray-800">
                  {new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({dayApts.length} appointment{dayApts.length !== 1 ? 's' : ''})
                  </span>
                </h4>
                <button onClick={() => setSelectedDay(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Blocked Date Info */}
              {dayBlockedInfo && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <p className="text-red-800 font-medium">
                        {dayBlockedInfo.is_entire_date_blocked
                          ? 'This entire date is blocked'
                          : `${dayBlockedInfo.blocked_time_count} time slot${dayBlockedInfo.blocked_time_count !== 1 ? 's are' : ' is'} blocked`}
                      </p>
                    </div>
                    <button onClick={() => unblockDate(dayBlockedInfo.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Unblock
                    </button>
                  </div>
                  <p className="text-red-600 text-sm mt-1"><strong>Reason:</strong> {dayBlockedInfo.reason}</p>
                  {!dayBlockedInfo.is_entire_date_blocked && dayBlockedInfo.blocked_times && (
                    <p className="text-red-600 text-sm mt-1">
                      <strong>Blocked times:</strong> {dayBlockedInfo.blocked_times.map(t => formatTime(t)).join(', ')}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-3">
                {dayApts.map(apt => renderAppointmentDetail(apt))}
                {dayApts.length === 0 && (
                  <p className="text-gray-500 text-center py-6">No appointments on this day</p>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // ========== Quick Filters ==========
  const renderQuickFilters = () => {
    const counts = {
      upcoming: yearAppointments.filter(apt => apt.status === 'scheduled').length,
      completed: yearAppointments.filter(apt => apt.status === 'completed').length,
      pendingPayment: yearAppointments.filter(apt => apt.payment_status === 'pending').length,
      paid: yearAppointments.filter(apt => apt.payment_status === 'paid').length
    };

    const buttons = [
      { label: `Upcoming (${counts.upcoming})`, onClick: () => { setStatusFilter('scheduled'); setPaymentFilter('all'); }, active: statusFilter === 'scheduled', color: 'bg-blue-100 text-blue-800 hover:bg-blue-200' },
      { label: `Completed (${counts.completed})`, onClick: () => { setStatusFilter('completed'); setPaymentFilter('all'); }, active: statusFilter === 'completed', color: 'bg-green-100 text-green-800 hover:bg-green-200' },
      { label: `Pending Payment (${counts.pendingPayment})`, onClick: () => { setStatusFilter('all'); setPaymentFilter('pending'); }, active: statusFilter === 'all' && paymentFilter === 'pending', color: 'bg-orange-100 text-orange-800 hover:bg-orange-200' },
      { label: `Paid (${counts.paid})`, onClick: () => { setStatusFilter('all'); setPaymentFilter('paid'); }, active: paymentFilter === 'paid', color: 'bg-green-100 text-green-800 hover:bg-green-200' },
      { label: 'All', onClick: () => { setStatusFilter('all'); setPaymentFilter('all'); }, active: statusFilter === 'all' && paymentFilter === 'all', color: 'bg-gray-100 text-gray-800 hover:bg-gray-200' }
    ];

    return (
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="text-sm font-medium text-gray-600 self-center mr-2">Quick Filters:</span>
        {buttons.map((button, index) => (
          <button key={index} onClick={button.onClick}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              button.active ? button.color.replace('hover:', '') + ' ring-2 ring-offset-1 ring-gray-300' : button.color
            }`}>
            {button.label}
          </button>
        ))}
      </div>
    );
  };

  // ========== Render ==========
  if (showNewAppointment) {
    return (
      <AdminNewAppointment
        services={services}
        appointments={appointments}
        coupons={coupons}
        bundles={bundles}
        blockedDates={blockedDates}
        customers={customers}
        createAppointment={createAppointment}
        adminSettings={adminSettings}
        onClose={() => setShowNewAppointment(false)}
        onSuccess={() => setRefreshKey(k => k + 1)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* New Appointment Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowNewAppointment(true)}
          className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center gap-2 shadow-lg"
        >
          <Plus className="w-5 h-5" />
          New Appointment
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Year's Appointments</p>
              <p className="text-2xl font-bold text-gray-900">{yearAppointments.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full"><Calendar className="w-6 h-6 text-blue-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Upcoming</p>
              <p className="text-2xl font-bold text-blue-600">{getUpcomingCount()}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full"><Clock className="w-6 h-6 text-blue-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{getCompletedCount()}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full"><CheckCircle className="w-6 h-6 text-green-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Payment</p>
              <p className="text-2xl font-bold text-orange-600">{getPendingPaymentCount()}</p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full"><AlertCircle className="w-6 h-6 text-orange-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Year's Revenue</p>
              <p className="text-2xl font-bold text-green-600">${getRevenue().toFixed(2)}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full"><DollarSign className="w-6 h-6 text-green-600" /></div>
          </div>
        </div>
      </div>

      {/* Appointments Management */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">Appointments</h3>
          <div className="flex gap-2 items-center">
            {/* Year Selector */}
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Years</option>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { id: 'list', icon: List, label: 'List' },
                { id: 'day', icon: Calendar, label: 'Day' },
                { id: 'week', icon: Calendar, label: 'Week' },
                { id: 'month', icon: Calendar, label: 'Month' }
              ].map(mode => (
                <button
                  key={mode.id}
                  onClick={() => setViewMode(mode.id)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === mode.id
                      ? 'bg-white text-green-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            <button
              onClick={refreshData}
              disabled={refreshing}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                refreshing
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
              title="Reload appointments from the database"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>

            <button
              onClick={syncGoogleCalendar}
              disabled={syncingCalendar}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                syncingCalendar
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
              }`}
              title="Sync all appointments with Google Calendar"
            >
              <RefreshCw size={14} className={syncingCalendar ? 'animate-spin' : ''} />
              {syncingCalendar ? 'Syncing...' : 'Sync Calendar'}
            </button>
            <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">Database Synced</div>
          </div>
        </div>

        {/* Calendar Sync Result */}
        {syncResult && (
          <div className={`mb-4 p-3 rounded-lg text-sm flex items-center justify-between ${
            syncResult.success
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <span>
              {syncResult.success
                ? syncResult.message
                : `Sync failed: ${syncResult.error}`}
              {syncResult.data?.errors?.length > 0 && (
                <span className="ml-2 text-orange-600">({syncResult.data.errors.length} warnings)</span>
              )}
            </span>
            <button onClick={() => setSyncResult(null)} className="ml-2 hover:opacity-70">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Calendar Navigation (for day/week/month views) */}
        {viewMode !== 'list' && (
          <div className="flex items-center justify-between mb-4 bg-gray-50 rounded-lg p-3">
            <button onClick={() => navigateCalendar(-1)}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <h4 className="text-lg font-semibold text-gray-800">{getCalendarTitle()}</h4>
              <button onClick={goToToday}
                className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors">
                Today
              </button>
            </div>
            <button onClick={() => navigateCalendar(1)}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Filters (always visible) */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="Search by name, email, or service..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
            </div>
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2">
            <option value="all">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No Show</option>
          </select>
          <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2">
            <option value="all">All Payments</option>
            <option value="pending">Payment Pending</option>
            <option value="paid">Paid</option>
          </select>
          <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2">
            <option value="all">All Services</option>
            {uniqueServices.map(service => (
              <option key={service} value={service}>{service}</option>
            ))}
          </select>
          {viewMode === 'list' && (
            <>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2">
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
                <option value="status">Sort by Status</option>
                <option value="payment">Sort by Payment</option>
                <option value="service">Sort by Service</option>
              </select>
              <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors"
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}>
                {sortOrder === 'asc' ? (
                  <><ArrowUp className="w-4 h-4" /><span className="text-sm">Asc</span></>
                ) : (
                  <><ArrowDown className="w-4 h-4" /><span className="text-sm">Desc</span></>
                )}
              </button>
            </>
          )}
        </div>

        {renderQuickFilters()}

        {/* View Content */}
        {viewMode === 'list' && (
          <>
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {filteredAppointments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {searchTerm || statusFilter !== 'all' || paymentFilter !== 'all' || serviceFilter !== 'all' ? 'No appointments match your filters' : 'No appointments scheduled'}
                </p>
              ) : (
                filteredAppointments.map(appointment => renderAppointmentDetail(appointment))
              )}
            </div>
            {(searchTerm || statusFilter !== 'all' || paymentFilter !== 'all') && (
              <div className="mt-4 text-sm text-gray-600 border-t pt-4">
                Showing {filteredAppointments.length} of {yearAppointments.length} appointments
                {filteredAppointments.length > 0 && (
                  <span className="ml-4">
                    Total value: ${filteredAppointments.reduce((sum, apt) => sum + (apt.payment_amount || apt.final_price || apt.service_price), 0).toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </>
        )}

        {viewMode === 'day' && renderDayView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
      </div>

      {/* Scheduled appointments under the calendar. Replaces the blocked-dates
          list that used to sit here - upcoming work is what you need at a glance;
          blocked dates moved into the collapsed section below. */}
      {viewMode !== 'list' && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
            <h3 className="text-xl font-bold text-gray-800">
              Scheduled Appointments ({scheduledUpcoming.length})
            </h3>
            <div className="flex bg-gray-100 rounded-lg p-1">
              {[
                { id: 'list', icon: List, label: 'List' },
                { id: 'cards', icon: LayoutGrid, label: 'Cards' }
              ].map(mode => {
                const Icon = mode.icon;
                return (
                  <button
                    key={mode.id}
                    onClick={() => chooseScheduledView(mode.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                      scheduledView === mode.id
                        ? 'bg-white text-green-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </div>

          {scheduledUpcoming.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No upcoming appointments scheduled.</p>
          ) : scheduledView === 'list' ? (
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {scheduledUpcoming.map(apt => (
                <button
                  key={apt.id}
                  onClick={() => setDetailAppointment(apt)}
                  className="w-full text-left py-3 px-2 hover:bg-gray-50 flex justify-between items-center gap-4"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 truncate">{apt.customer_name}</p>
                    <p className="text-sm text-gray-600 truncate">
                      {apt.service_names || apt.service_name}
                    </p>
                    {apt.service_address && (
                      <p className="text-xs text-gray-500 truncate">{apt.service_address}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-medium text-gray-800">{formatDateShort(apt.appointment_date)}</p>
                    <p className="text-sm text-gray-600">{formatTime(apt.appointment_time)}</p>
                    <p className="text-xs text-gray-500">
                      ${parseFloat(apt.final_price || apt.service_price || 0).toFixed(2)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-h-[600px] overflow-y-auto">
              {scheduledUpcoming.map(apt => (
                <button
                  key={apt.id}
                  onClick={() => setDetailAppointment(apt)}
                  className="text-left border border-gray-200 rounded-lg p-4 hover:border-green-400 hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <span className="font-semibold text-gray-800 truncate">{apt.customer_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 flex-shrink-0">
                      {apt.payment_status === 'paid' ? 'Paid' : 'Scheduled'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{apt.service_names || apt.service_name}</p>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                      {formatDateShort(apt.appointment_date)} at {formatTime(apt.appointment_time)}
                    </p>
                    {apt.customer_phone && (
                      <p className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                        {apt.customer_phone}
                      </p>
                    )}
                    {apt.service_address && (
                      <p className="flex items-start gap-1.5">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span className="truncate">{apt.service_address}</span>
                      </p>
                    )}
                  </div>
                  <p className="mt-3 font-semibold text-green-700">
                    ${parseFloat(apt.final_price || apt.service_price || 0).toFixed(2)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Blocked dates still need somewhere to be unblocked - this is the only
          screen that can do it - so they stay here, just collapsed by default. */}
      {viewMode !== 'list' && blockedDates && blockedDates.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <button
            onClick={() => setShowBlockedDates(v => !v)}
            className="w-full flex justify-between items-center text-left"
          >
            <h3 className="text-lg font-bold text-gray-800">
              Blocked Dates ({blockedDates.length})
            </h3>
            <span className="text-sm text-gray-500">{showBlockedDates ? 'Hide' : 'Show'}</span>
          </button>

          {showBlockedDates && (
            <div className="space-y-3 max-h-80 overflow-y-auto mt-4">
              {blockedDates
                .slice()
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map(blocked => (
                  <div key={blocked.id} className="flex justify-between items-center p-3 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-800">
                          {new Date(blocked.date + 'T00:00:00').toLocaleDateString('en-US', {
                            weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
                          })}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${blocked.is_entire_date_blocked
                          ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {blocked.is_entire_date_blocked ? 'Entire Date' : `${blocked.blocked_time_count} Times`}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600"><strong>Reason:</strong> {blocked.reason}</p>
                      {!blocked.is_entire_date_blocked && blocked.blocked_times && (
                        <p className="text-sm text-gray-500">
                          <strong>Blocked times:</strong> {blocked.blocked_times.map(t => formatTime(t)).join(', ')}
                        </p>
                      )}
                    </div>
                    <button onClick={() => unblockDate(blocked.id)}
                      className="text-red-600 hover:text-red-800 ml-3" title="Unblock this date">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              }
            </div>
          )}
        </div>
      )}

      {/* Appointment detail / edit modal. Reuses renderAppointmentDetail so the modal and
          the inline rows stay one implementation - it opens read-only with the
          Complete / Mark Paid / Edit / Delete actions, and the Edit button swaps
          in the same edit form used everywhere else. */}
      {detailLive && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            // A backdrop click mid-edit would silently discard typed changes.
            if (editingAppointment && editingAppointment.id === detailLive.id) return;
            closeDetailModal();
          }}
        >
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold text-gray-800">
                {editingAppointment && editingAppointment.id === detailLive.id
                  ? 'Edit Appointment'
                  : 'Appointment Details'}
              </h3>
              <button onClick={closeDetailModal} disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-600" title="Close">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4">
              {renderAppointmentDetail(detailLive)}
            </div>
          </div>
        </div>
      )}

      {/* Block Date Modal */}
      {showBlockDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                Block {selectedBlockDates.length} Date{selectedBlockDates.length !== 1 ? 's' : ''}
              </h3>
              <button onClick={() => setShowBlockDateModal(false)} disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Selected Dates:</label>
                <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {selectedBlockDates.map(dateStr =>
                    new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
                    })
                  ).join(', ')}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason *</label>
                <textarea value={blockDateForm.reason}
                  onChange={(e) => setBlockDateForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Why are you blocking these dates?"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  disabled={isSubmitting} rows="3" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Blocking Type</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3">
                    <input type="radio" name="blockingType" checked={blockDateForm.blockEntireDate}
                      onChange={() => setBlockDateForm(prev => ({ ...prev, blockEntireDate: true, selectedTimes: [] }))}
                      disabled={isSubmitting} className="text-red-600" />
                    <span className="font-medium">Block Entire Date</span>
                    <span className="text-sm text-gray-500">(No appointments allowed)</span>
                  </label>
                  <label className="flex items-center gap-3">
                    <input type="radio" name="blockingType" checked={!blockDateForm.blockEntireDate}
                      onChange={() => setBlockDateForm(prev => ({ ...prev, blockEntireDate: false }))}
                      disabled={isSubmitting} className="text-red-600" />
                    <span className="font-medium">Block Specific Times</span>
                    <span className="text-sm text-gray-500">(Select individual time slots)</span>
                  </label>
                </div>
              </div>

              {!blockDateForm.blockEntireDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Time Slots to Block ({blockDateForm.selectedTimes.length} selected)
                  </label>
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {TIME_SLOTS.map(timeStr => (
                      <button key={timeStr} type="button" onClick={() => handleTimeSelection(timeStr)}
                        disabled={isSubmitting}
                        className={`p-2 text-xs rounded border transition-colors ${
                          blockDateForm.selectedTimes.includes(timeStr)
                            ? 'bg-red-500 text-white border-red-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-red-50'
                        }`}>
                        {formatTime(timeStr)}
                      </button>
                    ))}
                  </div>
                  {blockDateForm.selectedTimes.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      <strong>Selected:</strong> {blockDateForm.selectedTimes.map(t => formatTime(t)).join(', ')}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button onClick={blockSelectedDates}
                  disabled={isSubmitting || !blockDateForm.reason.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg">
                  {isSubmitting ? 'Blocking...' : `Block ${selectedBlockDates.length} Date${selectedBlockDates.length !== 1 ? 's' : ''}`}
                </button>
                <button onClick={() => setShowBlockDateModal(false)} disabled={isSubmitting}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-lg">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAppointments;
